import { useEffect, useState } from 'react';
import { SyncService, type SyncState } from '@/services/sync';
import { Button } from '@/design-system/Button';
import { Card } from '@/design-system/Surfaces';

/**
 * Turning on sync (spec §15.3, §21).
 *
 * A parent account exists for exactly one reason: so the same family's work
 * appears on the iPad and on the laptop. It is not a profile, it is not a
 * social account, and it is not required to use the app — the whole product
 * works signed out, on one device, forever.
 *
 * The screen says which of those two states you are in, in plain words, and
 * never claims the other one.
 */
/**
 * True while this device is signed in to a sync service.
 *
 * Several screens make claims that are only true one way or the other — what
 * has left the device, what a deletion actually deletes. They ask rather than
 * assume, because both sentences are correct in one state and a lie in the
 * other.
 */
export function useSyncing(): boolean {
  const [state, setState] = useState<SyncState>(SyncService.state);
  useEffect(() => {
    const off = SyncService.subscribe(setState);
    return () => {
      off();
    };
  }, []);
  return state.kind !== 'off' && state.kind !== 'signed-out';
}

export function SyncPanel() {
  const [state, setState] = useState<SyncState>(SyncService.state);
  const [mode, setMode] = useState<'in' | 'up'>('in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const off = SyncService.subscribe(setState);
    return () => {
      off();
    };
  }, []);

  // Not configured: no sign-in, no switch, no "coming soon" (§7 C06-C07).
  if (!SyncService.available) {
    return (
      <Card>
        <h3 className="font-display text-[18px] font-semibold text-ink">This device only</h3>
        <p className="mt-m text-[16px] text-ink-soft">
          There is no account and no server in this build, so nothing your family makes leaves
          this device. That also means it is not backed up anywhere and it will not appear on a
          second device. Keepsake exports are how you keep a copy.
        </p>
      </Card>
    );
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const result = mode === 'in'
      ? await SyncService.signIn(email, password)
      : await SyncService.register(email, password);
    setBusy(false);
    if (!result.ok) setError(result.error);
    else setPassword('');
  };

  if (state.kind === 'signed-out') {
    return (
      <Card>
        <h3 className="font-display text-[18px] font-semibold text-ink">
          {mode === 'in' ? 'Sign in to sync' : 'Create a parent account'}
        </h3>
        <p className="mt-s text-[15px] text-ink-soft">
          One grown-up account per family. Children never sign in and never have one.
          Everything already on this device comes with you.
        </p>

        <form onSubmit={submit} className="mt-l space-y-m">
          <label className="block">
            <span className="font-display text-[15px] font-semibold text-ink-soft">Email</span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-xs w-full rounded-pebble border-2 border-line bg-card px-m py-s text-[16px]"
            />
          </label>
          <label className="block">
            <span className="font-display text-[15px] font-semibold text-ink-soft">Password</span>
            <input
              type="password"
              required
              minLength={10}
              autoComplete={mode === 'in' ? 'current-password' : 'new-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-xs w-full rounded-pebble border-2 border-line bg-card px-m py-s text-[16px]"
            />
            {mode === 'up' ? (
              <span className="mt-xs block text-[14px] text-ink-faint">
                At least 10 characters. Length is what matters — no symbol puzzles.
              </span>
            ) : null}
          </label>

          {error ? (
            <p role="alert" className="text-[15px] text-secondary">
              {error}
            </p>
          ) : null}

          <div className="flex flex-wrap items-center gap-m">
            <Button variant="magic" size="md" type="submit" loading={busy}>
              {mode === 'in' ? 'Sign in' : 'Create account'}
            </Button>
            <button
              type="button"
              onClick={() => {
                setMode(mode === 'in' ? 'up' : 'in');
                setError(null);
              }}
              className="min-h-touch font-display text-[15px] font-semibold text-primary underline underline-offset-4"
            >
              {mode === 'in' ? 'Create one instead' : 'I already have one'}
            </button>
          </div>
        </form>
      </Card>
    );
  }

  const label = describe(state);
  return (
    <Card>
      <h3 className="font-display text-[18px] font-semibold text-ink">Sync</h3>
      <p className="mt-s text-[16px] text-ink-soft">
        Signed in as <strong className="text-ink">{SyncService.session?.email}</strong>.
      </p>
      <p className="mt-m flex items-center gap-xs text-[15px] font-semibold" aria-live="polite">
        <span
          aria-hidden
          className={`inline-block h-2 w-2 rounded-full ${
            state.kind === 'failed' ? 'bg-secondary' : state.kind === 'idle' ? 'bg-leaf' : 'bg-ink-faint'
          }`}
        />
        <span className={state.kind === 'failed' ? 'text-secondary' : 'text-ink-soft'}>{label}</span>
      </p>

      <div className="mt-l flex flex-wrap gap-m">
        <Button variant="soft" size="md" onClick={() => void SyncService.run()}>
          Sync now
        </Button>
        <Button variant="outline" size="md" onClick={() => void SyncService.signOut()}>
          Sign out
        </Button>
      </div>

      <p className="mt-m text-[14px] text-ink-faint">
        Signing out leaves everything on this device exactly as it is. Nothing is deleted.
      </p>
    </Card>
  );
}

function describe(state: SyncState): string {
  switch (state.kind) {
    case 'working':
      return 'Syncing…';
    case 'idle':
      return state.pending === 0
        ? 'Everything on this device is on the server too.'
        : `${state.pending} change${state.pending === 1 ? '' : 's'} still to send.`;
    case 'offline':
      return state.pending === 0
        ? 'Offline. Nothing is waiting to be sent.'
        : `Offline — ${state.pending} change${state.pending === 1 ? '' : 's'} waiting. They will go when you are back.`;
    case 'failed':
      return state.message;
    default:
      return 'Not syncing.';
  }
}
